// packages/shared/src/models/person.ts

export enum OfficerType {
  NONE = "None",
  PRESIDENT = "President",
  VICE_PRESIDENT = "Vice President",
  SECRETARY = "Secretary",
  TREASURER = "Treasurer"
}

// Shareholder model
export interface Person {
  legalName: string;
  email: string;
  taxId: string;
  addressLines: string;
  city: string;
  state: string;
  zip: string;
  percentOwned?: number | null;
  dob: string;
  fullAddress?: string;
  officerTypes: OfficerType[];
  officerType?: OfficerType; // Legacy field for backward compatibility
  isShareholder: boolean;
  isDirector: boolean;
  relationship: string;
  otherRelationship: string;
}

/**
 * Creates a default Person object
 */
export function createDefaultPerson(): Person {
  return {
    legalName: "",
    email: "",
    taxId: "",
    addressLines: "",
    city: "",
    state: "",
    zip: "",
    percentOwned: 0,
    dob: "",
    officerTypes: [],
    isShareholder: false,
    isDirector: false,
    relationship: "",
    otherRelationship: ""
  };
}

/**
 * Validates that no two people have the same officer type
 */
export function validateUniqueOfficerTypes(people: Person[]): { isValid: boolean; duplicates: OfficerType[] } {
  const officerTypeCounts = new Map<OfficerType, number>();
  
  // Count occurrences of each officer type across all people
  people.forEach(person => {
    person.officerTypes.forEach(officerType => {
      if (officerType !== OfficerType.NONE) {
        officerTypeCounts.set(officerType, (officerTypeCounts.get(officerType) || 0) + 1);
      }
    });
  });
  
  // Find duplicates
  const duplicates: OfficerType[] = [];
  officerTypeCounts.forEach((count, officerType) => {
    if (count > 1) {
      duplicates.push(officerType);
    }
  });
  
  return {
    isValid: duplicates.length === 0,
    duplicates
  };
}

/**
 * Validates that president and secretary are not the same person
 */
export function validatePresidentSecretaryDifferent(people: Person[]): { isValid: boolean; conflictPerson?: Person } {
  for (const person of people) {
    const hasPresident = person.officerTypes.includes(OfficerType.PRESIDENT);
    const hasSecretary = person.officerTypes.includes(OfficerType.SECRETARY);
    
    if (hasPresident && hasSecretary) {
      return {
        isValid: false,
        conflictPerson: person
      };
    }
  }
  
  return { isValid: true };
}

/**
 * Validates all officer type business rules
 */
export function validateOfficerRules(people: Person[]): { 
  isValid: boolean; 
  errors: string[] 
} {
  const errors: string[] = [];
  
  // Check for unique officer types
  const uniqueValidation = validateUniqueOfficerTypes(people);
  if (!uniqueValidation.isValid) {
    errors.push(`Multiple people cannot have the same officer type(s): ${uniqueValidation.duplicates.join(', ')}`);
  }
  
  // Check president/secretary conflict
  const presSecValidation = validatePresidentSecretaryDifferent(people);
  if (!presSecValidation.isValid) {
    errors.push(`The same person cannot be both President and Secretary: ${presSecValidation.conflictPerson?.legalName || 'Unknown person'}`);
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
}